import { ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import Skeleton from "react-loading-skeleton";
import { classNames } from "@ct-react/core";
import { useLocaleUrl } from "@ct-react/locale";
import { castBookingOption } from "@ct-react/calendar";
import { ArticleType } from "@shared/url";
import { AnyArticleSortInput, SearchResult } from "@shared/graphql";
import { articleTypes } from "../i18n/sharable-defs";
import { formatSort, useGQLSearchFormatter } from "../hooks/format-gql-variables";
import { useMediaQueryDown } from "../hooks/media-queries";
import { PaginationPayload } from "../models/search";
import * as criteriaTools from "../tools/search-params/search-criteria";
import * as paginationTools from "../tools/search-params/search-pagination";
import { useSearchContext } from "../contexts/search";
import { Wrapper } from "../components/layout/wrapper";
import ArticleCard from "../components/article-card";
import SearchedPagination from "../components/search/pagination";
import SearchFooterContent from "../components/search/footer-content";
import SeoMetas from "../components/layout/seo-metas";
import SearchSort from "../components/search/sort";
import "./search.scss";

const transDefs = defineMessages({
  count: { id: "search.results.count", defaultMessage: "{count, plural, =0 {# résultat} =1 {# résultat} other {# résultats}}" },
  empty: { id: "search.results.empty", defaultMessage: "Aucun bien ne correspond actuellement à votre recherche. Essayez d'élargir vos critères ou <link>contactez-nous</link>, nous pourrons vous proposer des biens similaires." }
});

const ANY_SEARCH_GQL_DATA = gql`
  query Search($where: AnyArticleFilterInput, $sort: AnyArticleSortInput, $first: Int, $last: Int, $before: String, $after: String) {
    search(where: $where, sort: $sort, first: $first, last: $last, before: $before, after: $after) {
      totalCount
      edges {
        cursor
        node {
          ... on RentalAccommodationArticle {
            id slug
            novelty recommended promoted
            images @limit(max: 10) { assets { url breakpoint } }
            address { city }
            title { value }
            lowestPrice { type onDemand value }
            features { maxGuests bedrooms bathrooms }
          }
          ... on SaleAccommodationArticle {
            id slug
            novelty recommended promoted sold reserved
            images @limit(max: 10) { assets { url breakpoint } }
            address { city }
            title { value }
            price { onDemand value }
            features { rooms bedrooms bathrooms livingSpace plotSpace }
          }
        }
        accommodationBookingSuggestion {
          period bookable priceOnDemand
          price { amount detailed { bare discount providerCharge { amount adaptive } } }
        }
      }
      paginationInfo {
        currentPage pageCount pageSize hasNextPage hasPreviousPage
        lastPageCursor nextPageCursor previousPageCursor linkPageCursors { cursor page }
      }
    }
  }
`;

const SearchPage = ({ type }: { type: ArticleType | "sold" }) => {

  const intl = useIntl();
  const navigate = useNavigate();
  const { buildLocaleHref } = useLocaleUrl();
  const isDisplayUntilTablet = useMediaQueryDown("mobile");

  const pageSize = useRef<number>(8);
  const [ fillSearchGridOddGap, setFillSearchGridOddGap ] = useState<boolean>(false);

  const [ searchParams, setSearchParams ] = useSearchParams();
  const [ ctx, dispatch ] = useSearchContext();

  const { where, ...others } = useGQLSearchFormatter(pageSize.current, type, searchParams);
  const { data, loading } = useQuery<{ search: SearchResult }>(ANY_SEARCH_GQL_DATA, {
    ssr: false,
    variables: { where, sort: formatSort(type, [ ctx[type].sort ]), ...others }
  });

  useEffect(() => {
    if (searchParams.size > 0 || Object.keys(ctx[type].saving).length === 0)
      dispatch({ type, filter: criteriaTools.parse(searchParams, type)});
    else
      navigate(`?${criteriaTools.rebuild(ctx[type].saving).toString()}`, { replace: true });
  }, []);

  useEffect(() => {
    const countAll = data?.search?.edges.length || 0;
    const count = (data?.search?.edges || []).reduce((acc, val) => acc + +val.node.recommended, 0);
    const check = count === 0 ? countAll % 2 !== 0 : (countAll - count) % 2 !== 0;
    setFillSearchGridOddGap(check && !isDisplayUntilTablet && type !== "sold");
  }, [ data, isDisplayUntilTablet ]);

  const onPaginate = (value: PaginationPayload) => {
    paginationTools.rebuild(value, searchParams);
    setSearchParams(searchParams);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const onSorting = (sort: AnyArticleSortInput) => {
    dispatch({ type, sort });
    paginationTools.rebuild({ direction: "after" }, searchParams);
    setSearchParams(searchParams);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }

  const wrapperClasses= classNames("search-wrapper", "content");
  const resultClasses = classNames("search-results");
  const listClasses = classNames("search-listed", { empty: !loading && data?.search.totalCount === 0 })

  return (
    <>

      <SeoMetas title={`${intl.formatMessage(articleTypes.page[type])} | EV Real Estate`} />

      <Wrapper.Centered key={type}
                        className={wrapperClasses}>

        <div className="searched-head">
          <h1>{intl.formatMessage(articleTypes.page[type])}</h1>
          <div className="counted">
            {loading && <Skeleton width={100} />}
            {!!data?.search && intl.formatMessage(transDefs.count, { count: data!.search!.totalCount })}
          </div>
          {(data?.search?.totalCount || 0) > 0 &&
            <SearchSort type={type} criteria={ctx[type].sort} onCriteriaChange={onSorting} />
          }
        </div>

        <main className={resultClasses}>
          <div className={listClasses}>
            {(data?.search.edges || Array.from({ length: pageSize.current })).map((edge: any, index: number) => (
              <ArticleCard key={edge?.node.id || index}
                           loading={loading}
                           className={classNames({ "exclusive": !!edge?.node.recommended })}
                           data={{
                             ...edge?.node,
                             ...(!!edge?.accommodationBookingSuggestion) && {
                               suggestion: castBookingOption(edge.accommodationBookingSuggestion)
                             },
                             ...(!!edge?.node.recommended && !isDisplayUntilTablet) && { searchExclusivity: true },
                             untargetable: type === "sold",
                             withInnerContextNav: true
                           }} />))
            }
            {fillSearchGridOddGap &&
              <article className="card-complement">
                <SearchFooterContent searchState={ctx[type].saving}
                                     mode="card" />
              </article>
            }
            {data?.search.totalCount === 0 &&
              <p>
                {intl.formatMessage(transDefs.empty, { link: (chunks: ReactNode) =>
                    <a href={buildLocaleHref("/contact")}>{chunks}</a>
                })}
              </p>
            }
          </div>
          {!!data?.search &&
            <SearchedPagination data={data.search.paginationInfo}
                                onPaginate={onPaginate} />
          }
        </main>

      </Wrapper.Centered>

      {(!fillSearchGridOddGap && type !== "sold") &&
        <section className="search-footer">
          <Wrapper.Centered>
            <SearchFooterContent searchState={ctx[type].saving}
                                 mode="footer" />
          </Wrapper.Centered>
        </section>
      }

    </>);

};

export default SearchPage;
